import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, BACKEND_URL, formatApiError } from "@/lib/api";
import { fmtMxn } from "@/context/CartContext";
import { toast } from "sonner";
import { ChevronLeft, Download, RotateCcw, CreditCard, Info, Loader2 } from "lucide-react";

export default function OrdenDetalle() {
  const { user } = useAuth();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [elig, setElig] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [showRefund, setShowRefund] = useState(false);

  const loadAll = async () => {
    const [o, e, r] = await Promise.all([
      api.get(`/alumno/orders/${orderId}`),
      api.get(`/alumno/orders/${orderId}/refund-eligibility`).catch(() => ({ data: null })),
      api.get(`/alumno/orders/${orderId}/refund-requests`).catch(() => ({ data: { items: [] } })),
    ]);
    setOrder(o.data); setElig(e.data); setRefunds(r.data.items || []);
  };
  useEffect(() => { loadAll(); }, [orderId]);

  const downloadInvoice = async () => {
    try {
      const r = await api.get(`/alumno/orders/${orderId}/invoice.pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `factura-${order.order_number}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  if (!order) return <AlumnoLayout user={user}><p className="text-gray-400">Cargando...</p></AlumnoLayout>;

  const activeRefund = refunds.find(r => ["auto_approved", "pending_review", "processed"].includes(r.status));
  const canRequest = elig?.eligible_manual && !activeRefund && order.status === "paid";
  return (
    <AlumnoLayout user={user}>
      <div>
        <Link to="/app/alumno/ordenes" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white mb-3">
          <ChevronLeft className="w-3 h-3" /> Volver
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Orden</p>
            <h1 className="mt-1 text-2xl font-bold text-white font-mono" data-testid="order-detail-number">{order.order_number}</h1>
            <p className="mt-1 text-xs text-gray-400">{order.created_at?.slice(0, 19).replace("T", " ")}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {order.status === "paid" && (
              <button onClick={downloadInvoice} data-testid="download-invoice-btn"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-white/5">
                <Download className="w-3 h-3" /> Descargar factura
              </button>
            )}
            {(order.status === "paid" || order.status === "partially_refunded") && (
              <div className="relative">
                <button onClick={() => canRequest && setShowRefund(true)}
                        disabled={!canRequest}
                        data-testid="order-refund-btn"
                        title={!canRequest ? "Ya existe una solicitud o el reembolso no está disponible." : ""}
                        className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded ${canRequest ? "bg-white/5 border border-white/20 text-white hover:bg-white/10" : "bg-white/[0.03] border border-white/10 text-gray-500 cursor-not-allowed"}`}>
                  <RotateCcw className="w-3 h-3" /> Solicitar reembolso
                </button>
              </div>
            )}
          </div>
        </div>

        {order.installments_plan && (
          <div className="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-200 inline-flex items-center gap-2" data-testid="order-msi-badge">
            <CreditCard className="w-4 h-4" />
            {order.installments_plan.months} pagos de ${order.installments_plan.monthly_amount_mxn?.toLocaleString("es-MX")} sin intereses ·
            provider {order.installments_plan.provider}
          </div>
        )}

        {activeRefund && (
          <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200 inline-flex items-center gap-2" data-testid="order-refund-status">
            <Info className="w-4 h-4" />
            Reembolso {activeRefund.status.replace("_", " ")}
            {activeRefund.auto_approve_reason && ` · ${activeRefund.auto_approve_reason}`}
          </div>
        )}

        <section className="mt-6 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 p-4 bg-[#111827] border border-gray-800 rounded-lg" data-testid="order-items">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Programas</p>
            <div className="mt-2 space-y-3">
              {(order.items || []).map((it, i) => (
                <div key={i} className="p-3 bg-[#0B0F14] border border-gray-800 rounded">
                  <p className="text-white font-semibold">{it.program_snapshot.title}</p>
                  <p className="text-xs text-gray-400 mt-1">Cohorte {it.cohort_snapshot?.name} · {it.cohort_snapshot?.start_date}</p>
                  <p className="mt-1 font-mono text-xs text-white">{fmtMxn(it.unit_price_mxn)}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="p-4 bg-[#111827] border border-gray-800 rounded-lg space-y-2 text-sm" data-testid="order-summary">
            <Row k="Subtotal" v={fmtMxn(order.subtotal_mxn)} />
            {order.discount_mxn > 0 && (<Row k={`Descuento ${order.discount_code || ""}`} v={`-${fmtMxn(order.discount_mxn)}`} className="text-emerald-300" />)}
            {order.tax_mxn ? <Row k="IVA 16%" v={fmtMxn(order.tax_mxn)} /> : null}
            <div className="pt-2 border-t border-gray-800">
              <div className="flex justify-between items-center">
                <span className="text-xs uppercase tracking-widest text-gray-400">Total</span>
                <span className="text-lg font-bold text-[#F26A21] font-mono">{fmtMxn(order.total_mxn)}</span>
              </div>
            </div>
            <p className="text-[10px] text-gray-500 pt-2 border-t border-gray-800">
              Status: <span className="text-white uppercase">{order.status}</span>
            </p>
            {elig?.refund_deadline_at && (
              <p className="text-[10px] text-gray-500">
                Garantía hasta {new Date(elig.refund_deadline_at).toLocaleDateString("es-MX")} · avance {elig.progress_pct || 0}%
              </p>
            )}
          </div>
        </section>

        {refunds.length > 0 && (
          <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="refund-history">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">Historial de solicitudes</p>
            <ul className="mt-2 text-xs text-gray-300 space-y-1">
              {refunds.map(r => (
                <li key={r.id}>
                  <span className="uppercase text-[10px] font-bold">{r.status}</span> · ${r.amount_mxn?.toLocaleString("es-MX")} · {new Date(r.created_at).toLocaleDateString("es-MX")}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {showRefund && (
        <RefundModal orderId={orderId} elig={elig}
                     onClose={() => setShowRefund(false)}
                     onSubmitted={() => { setShowRefund(false); loadAll(); }} />
      )}
    </AlumnoLayout>
  );
}

function Row({ k, v, className = "" }) {
  return (
    <div className={`flex justify-between ${className}`}>
      <span className="text-gray-400">{k}</span>
      <span className="text-white font-mono">{v}</span>
    </div>
  );
}

function RefundModal({ orderId, elig, onClose, onSubmitted }) {
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState("no_encaja");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const auto = elig?.eligible_auto;

  const submit = async () => {
    if (text.trim().length < 10) { toast.error("Cuéntanos al menos 10 caracteres."); return; }
    setSending(true);
    try {
      const r = await api.post(`/alumno/orders/${orderId}/refund-request`, {
        reason_category: category, reason_text: text.trim(),
      });
      if (r.data.status === "auto_approved" || r.data.status === "processed") {
        toast.success("Reembolso aprobado. Verás el crédito en 5-10 días hábiles.");
      } else {
        toast.success("Solicitud enviada. Un asesor te contactará en 48h.");
      }
      onSubmitted();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           data-testid="refund-modal"
           className="w-full max-w-lg rounded-2xl bg-[#111827] border border-white/10 p-6 max-h-[92vh] overflow-y-auto">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Reembolso</p>
        <h2 className="mt-1 text-xl font-bold text-white">{step === 1 ? "Cuéntanos qué pasó" : "Confirma tu solicitud"}</h2>
        {step === 1 && (
          <>
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-300 mb-2">Motivo</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "no_encaja", l: "No me encaja" },
                  { v: "contenido_no_lo_esperado", l: "Contenido no esperado" },
                  { v: "problema_tecnico", l: "Problema técnico" },
                  { v: "otro", l: "Otro" },
                ].map(o => (
                  <button key={o.v} onClick={() => setCategory(o.v)}
                          data-testid={`refund-cat-${o.v}`}
                          className={`text-left rounded-md border p-2 text-xs ${category === o.v ? "border-[#F26A21] bg-[#F26A21]/10 text-white" : "border-white/10 text-gray-300 hover:bg-white/5"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <label className="block mt-4">
              <span className="block text-xs font-semibold text-gray-300 mb-1">Descripción (mín 10 caracteres)</span>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={2000}
                        data-testid="refund-text-input"
                        className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
                        placeholder="Cuéntanos con más detalle para procesar tu caso…" />
            </label>
          </>
        )}
        {step === 2 && (
          <div className="mt-4 space-y-3">
            <div className="rounded-2xl border border-white/10 bg-black/30 p-3 text-xs text-gray-300 space-y-1">
              <p><span className="text-gray-500">Motivo:</span> {category}</p>
              <p><span className="text-gray-500">Descripción:</span> {text}</p>
              <p><span className="text-gray-500">Avance actual:</span> {elig?.progress_pct || 0}%</p>
            </div>
            {auto ? (
              <p className="text-sm text-emerald-300">Tu solicitud entra en garantía DCC 30 días y se aprobará automáticamente al confirmar.</p>
            ) : (
              <p className="text-sm text-amber-300">Tu solicitud se enviará a revisión editorial. Recibirás respuesta en ≤48h.</p>
            )}
          </div>
        )}
        <div className="mt-6 flex justify-between">
          <button onClick={onClose} className="text-xs text-gray-400 hover:text-white">Cancelar</button>
          {step === 1 ? (
            <button onClick={() => text.trim().length >= 10 && setStep(2)}
                    disabled={text.trim().length < 10}
                    data-testid="refund-next-btn"
                    className="px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50">
              Continuar →
            </button>
          ) : (
            <button onClick={submit} disabled={sending}
                    data-testid="refund-submit-btn"
                    className="px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {auto ? "Confirmar reembolso" : "Enviar solicitud"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
